import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { LoginComponent } from '../../default/login/login.component';
import { AuthService } from '../../default/login/service/login-service/auth.service';
import { ConfigService } from '../../../services/config.service';
import { AdminAuthService } from '../../../services/admin-auth.service';
import { ErrorService } from '../../../shared/services/error.service';
import { ImageTagComponent, ButtonComponent, InputComponent } from '../../ui';
import { LOGIN_TYPE } from '../../enums/common-enums/common-enums';
import { ResetPasswordDialogComponent } from '../reset-password-dialog/reset-password-dialog.component';
import { ChangePasswordDialogComponent } from '../change-password-dialog/change-password-dialog.component';

@Component({
  selector: 'app-admin-login',
  imports: [CommonModule, ReactiveFormsModule, ImageTagComponent, ButtonComponent, InputComponent, RouterLink],
  templateUrl: './admin-login.component.html',
  styleUrl: './admin-login.component.css'
})
export class AdminLoginComponent extends LoginComponent implements OnInit {
  private adminAuthService = inject(AdminAuthService);
  private dialog = inject(MatDialog);

  constructor(
    fb: FormBuilder,
    authService: AuthService,
    configService: ConfigService,
    router: Router
  ) {
    super(fb, authService, configService, router);
  }

  override ngOnInit(): void {
    // Force email-only login type for Platform Admin
    this.usernameType.set(LOGIN_TYPE.EMAIL);
    // Override form to use email validation only
    this.loginForm = this.fb.group({
      username: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]]
    });
    // Don't call super.ngOnInit() to prevent loadConfig() from being called
  }

  override switchUsernameType(type: 'email' | 'mobile'): void {
    // Prevent switching to mobile for Platform Admin
    // Always keep it as email
    this.usernameType.set(LOGIN_TYPE.EMAIL);
  }

  override onSubmit(): void {
    if (this.loginForm.valid) {
      const username = this.loginForm.get('username')?.value;
      if (!this.isEmail(username)) {
        this.loginForm.get('username')?.setErrors({ email: true });
        return;
      }
      
      this.isLoading.set(true);
      const { username: email, password } = this.loginForm.value;
      
      // Call admin authentication API
      this.adminAuthService.login(email, password).subscribe({
        next: (response) => {
          // Login successful
          this.isLoading.set(false);
          
          // Check if this is first-time login (password reset required)
          // Assuming the API response includes a field like isFirstTimeLogin or passwordResetRequired
          // const isFirstTimeLogin = response.data?.isFirstLogin || false;
          
          // if (isFirstTimeLogin) {
          //   // Show reset password dialog
          //   const dialogRef = this.dialog.open(ResetPasswordDialogComponent, {
          //     width: '500px',
          //     disableClose: true, // Prevent closing without resetting password
          //     data: { email }
          //   });

          //   dialogRef.afterClosed().subscribe((result) => {
          //     if (result === true) {
          //       // Password reset successful, user needs to login again
          //       this.errorService.showSuccess('Password reset successfully. Please login with your new password.');
          //       // Clear form
          //       this.loginForm.reset();
          //     } else {
          //       // User cancelled or closed dialog, logout and show message
          //       this.adminAuthService.logout().subscribe(() => {
          //         this.errorService.showError('Password reset is required for first-time login. Please login again and reset your password.');
          //       });
          //     }
          //   });
          // } 
          // else
             {
            // Get user's name from response
            const userName = response.data?.user?.name || email;
            const welcomeMessage = `Welcome, ${userName}! Login successful.`;
            
            // Show success toast message with user's name
            this.errorService.showSuccess(welcomeMessage);
            
            // Navigate to admin dashboard
            this.router.navigate(['/admin/dashboard']);
            
            // Check if this is first-time login
            const isFirstTimeLogin = response.data?.isFirstLogin || response.data?.user?.isFirstTimeLogin || false;
            
            // Store isFirstTimeLogin flag in localStorage to persist across page refresh
            // The dialog will be opened by admin-layout component after navigation
            if (isFirstTimeLogin) {
              localStorage.setItem('adminIsFirstTimeLogin', 'true');
            }
          }
        },
        error: (error) => {
          // Handle login error
          this.isLoading.set(false);
          const errorMessage = error.message || 'Invalid credentials. Please try again.';
          this.errorService.showError(errorMessage);
        }
      });
    } else {
      Object.keys(this.loginForm.controls).forEach(key => {
        this.loginForm.get(key)?.markAsTouched();
      });
    }
  }

  override get companyName(): string {
    return 'Platform Admin';
  }

  override get poweredBy(): string {
    return 'HCMS Platform';
  }
}
